import React, { useEffect, useState, useRef, useLayoutEffect } from 'react';
import Timecode from 'smpte-timecode';

const DEFAULT_SETTINGS = {
  frameRate: 29.97,
  dropFrame: false,
  refreshRate: null,
  refreshRateMode: 'auto',
  textColor: '#eee',
  borderColor: 'transparent',
  borderThickness: 0,
  timeSource: 'utc',
  timeApiUrl: 'https://sync.meetmo.io/time',
  syncMode: 'single',
  syncIntervalSec: 30,
  syncSamples: 3,
  syncSmoothing: 0.2,
  fixedStart: '',
  tcOffsetMs: 0,
  tcOffsetFrames: 0,
  markerInterval: 'off',
  markerDurationMs: 120,
  markerColor: '#f00',
  frameBlink: false,
  probeMode: false,
  probeIntervalMs: 1000,
  posX: 50,
  posY: 50,
  anchor: 'center',
  safePadding: 0,
  bgColor: '#000',
  bgOpacity: 0,
  bgPadding: 0,
  bgRadius: 0,
  fontFamily: 'Arial, sans-serif',
  fontWeight: '700',
  scaleFactor: 1,
  showTime: true,
  showFrames: true,
  showMs: true,
  showDiagnostics: false,
  showDropFrameIndicator: true,
  freeze: false,
  holdOnClick: false,
  showUI: true,
};

const VALID_DROP_FRAME_RATES = [23.976, 29.97, 59.94];
const REFRESH_RATE_OPTIONS = [
  23.976,
  24,
  25,
  29.97,
  30,
  50,
  59.94,
  60,
  75,
  90,
  100,
  120,
  144,
  165,
  240,
  360,
];

const FONT_OPTIONS = [
  { label: 'System UI', value: 'system-ui, sans-serif' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Inter', value: 'Inter, sans-serif' },
  { label: 'Helvetica', value: 'Helvetica, Arial, sans-serif' },
  { label: 'Roboto', value: 'Roboto, Arial, sans-serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
  { label: 'Tahoma', value: 'Tahoma, sans-serif' },
  { label: 'Courier New', value: '"Courier New", monospace' },
  { label: 'Consolas', value: 'Consolas, monospace' },
  { label: 'Menlo', value: 'Menlo, monospace' },
  { label: 'Monospace', value: 'monospace' },
];

const FONT_WEIGHT_OPTIONS = [
  { label: '300', value: '300' },
  { label: '400', value: '400' },
  { label: '500', value: '500' },
  { label: '600', value: '600' },
  { label: '700', value: '700' },
  { label: '800', value: '800' },
  { label: '900', value: '900' },
  { label: 'Normal', value: 'normal' },
  { label: 'Bold', value: 'bold' },
];

const ANCHOR_TRANSLATE = {
  center: ['-50%', '-50%'],
  'top-left': ['0%', '0%'],
  top: ['-50%', '0%'],
  'top-right': ['-100%', '0%'],
  left: ['0%', '-50%'],
  right: ['-100%', '-50%'],
  'bottom-left': ['0%', '-100%'],
  bottom: ['-50%', '-100%'],
  'bottom-right': ['-100%', '-100%'],
};

function parseNumber(value, fallback) {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num : fallback;
}

function clampNumber(value, min, max, fallback) {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, value));
}

function parseBool(value, fallback) {
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
}

function parseEnum(value, options, fallback) {
  if (options.includes(value)) return value;
  return fallback;
}

function parseFixedStart(value) {
  if (value == null || value === '') return Date.now();
  const asNumber = parseFloat(value);
  if (Number.isFinite(asNumber)) return asNumber;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? Date.now() : parsed;
}

function parseTimeApiUrl(value, fallback) {
  if (!value) return fallback;
  try {
    const url = new URL(value);
    return url.toString();
  } catch (error) {
    return fallback;
  }
}

function normalizeHexColor(value, fallback) {
  if (!value) return fallback;
  const shortMatch = value.match(/^#([0-9a-f]{3})$/i);
  if (shortMatch) {
    const hex = shortMatch[1]
      .split('')
      .map((c) => c + c)
      .join('');
    return `#${hex}`;
  }
  const fullMatch = value.match(/^#([0-9a-f]{6})$/i);
  if (fullMatch) return value.toLowerCase();
  return fallback;
}

function applyOpacity(color, opacity) {
  if (color == null) return color;
  const clamped = clampNumber(opacity, 0, 1, 1);
  if (color === 'transparent') return 'transparent';
  const hexMatch = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hexMatch) {
    let hex = hexMatch[1];
    if (hex.length === 3) {
      hex = hex
        .split('')
        .map((c) => c + c)
        .join('');
    }
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${clamped})`;
  }
  const rgbMatch = color.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/i);
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10);
    const g = parseInt(rgbMatch[2], 10);
    const b = parseInt(rgbMatch[3], 10);
    return `rgba(${r}, ${g}, ${b}, ${clamped})`;
  }
  const rgbaMatch = color.match(
    /^rgba\((\d+),\s*(\d+),\s*(\d+),\s*([0-9.]+)\)$/i
  );
  if (rgbaMatch) {
    const r = parseInt(rgbaMatch[1], 10);
    const g = parseInt(rgbaMatch[2], 10);
    const b = parseInt(rgbaMatch[3], 10);
    return `rgba(${r}, ${g}, ${b}, ${clamped})`;
  }
  return color;
}

function formatTimecode(tcString, showTime, showFrames) {
  if (!tcString) return '';
  const match = tcString.match(/^(\d\d:\d\d:\d\d)([:;])(\d+)$/);
  if (!match) return tcString;
  const timePart = showTime ? match[1] : '';
  const framePart = showFrames ? match[3] : '';
  if (showTime && showFrames) return `${match[1]}${match[2]}${match[3]}`;
  if (showTime) return timePart;
  if (showFrames) return framePart;
  return '';
}

function HelpIcon({ text }) {
  return (
    <span className="help-icon" title={text} aria-label={text} role="img">
      !
    </span>
  );
}

function extractServerTimeMs(data) {
  if (!data) return null;
  if (data.iso8601) {
    const parsed = Date.parse(data.iso8601);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (data.utc_datetime) {
    const parsed = Date.parse(data.utc_datetime);
    return Number.isNaN(parsed) ? null : parsed;
  }
  if (Number.isFinite(data.timestamp)) {
    return data.timestamp * 1000;
  }
  return null;
}

async function fetchServerTimeSample(url) {
  const t0 = performance.now();
  const response = await fetch(url, { cache: 'no-store' });
  const t1 = performance.now();
  if (!response.ok) throw new Error('Network response not ok');
  const data = await response.json();
  const serverTimeMs = extractServerTimeMs(data);
  if (serverTimeMs == null) throw new Error('Invalid time response');
  return {
    serverTimeMs,
    rttMs: t1 - t0,
    clientNowMs: Date.now(),
    clientNowPerf: performance.now(),
  };
}

function parseSettingsFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const settings = { ...DEFAULT_SETTINGS };

  const frameRateParsed = parseNumber(
    params.get('frameRate'),
    DEFAULT_SETTINGS.frameRate
  );
  settings.frameRate =
    frameRateParsed > 0 ? frameRateParsed : DEFAULT_SETTINGS.frameRate;
  settings.dropFrame = parseBool(
    params.get('dropFrame'),
    DEFAULT_SETTINGS.dropFrame
  );
  const refreshRateModeParam = params.get('refreshRateMode');
  settings.refreshRateMode = parseEnum(
    refreshRateModeParam,
    ['auto', 'manual'],
    DEFAULT_SETTINGS.refreshRateMode || 'auto'
  );
  const refreshRateParam = params.get('refreshRate');
  const refreshRateParsed =
    refreshRateParam == null || refreshRateParam === ''
      ? null
      : parseNumber(refreshRateParam, null);
  settings.refreshRate =
    refreshRateParsed != null && refreshRateParsed > 0 ? refreshRateParsed : null;

  settings.textColor = params.get('textColor') || DEFAULT_SETTINGS.textColor;
  settings.borderColor =
    params.get('borderColor') || DEFAULT_SETTINGS.borderColor;
  settings.borderThickness = parseNumber(
    params.get('borderThickness'),
    DEFAULT_SETTINGS.borderThickness
  );
  settings.timeSource = parseEnum(
    params.get('timeSource'),
    ['utc', 'local', 'fixed'],
    DEFAULT_SETTINGS.timeSource
  );
  settings.timeApiUrl = parseTimeApiUrl(
    params.get('timeApiUrl'),
    DEFAULT_SETTINGS.timeApiUrl
  );
  settings.syncMode = parseEnum(
    params.get('syncMode'),
    ['single', 'periodic'],
    DEFAULT_SETTINGS.syncMode
  );
  settings.syncIntervalSec = parseNumber(
    params.get('syncIntervalSec'),
    DEFAULT_SETTINGS.syncIntervalSec
  );
  settings.syncSamples = parseNumber(
    params.get('syncSamples'),
    DEFAULT_SETTINGS.syncSamples
  );
  settings.syncSmoothing = clampNumber(
    parseNumber(params.get('syncSmoothing'), DEFAULT_SETTINGS.syncSmoothing),
    0,
    1,
    DEFAULT_SETTINGS.syncSmoothing
  );
  settings.fixedStart = params.get('fixedStart') || DEFAULT_SETTINGS.fixedStart;
  settings.tcOffsetMs = parseNumber(
    params.get('tcOffsetMs'),
    DEFAULT_SETTINGS.tcOffsetMs
  );
  settings.tcOffsetFrames = parseNumber(
    params.get('tcOffsetFrames'),
    DEFAULT_SETTINGS.tcOffsetFrames
  );
  settings.markerInterval = parseEnum(
    params.get('markerInterval'),
    ['off', '1', '5', '60'],
    DEFAULT_SETTINGS.markerInterval
  );
  settings.markerDurationMs = parseNumber(
    params.get('markerDurationMs'),
    DEFAULT_SETTINGS.markerDurationMs
  );
  settings.markerColor = params.get('markerColor') || DEFAULT_SETTINGS.markerColor;
  settings.frameBlink = parseBool(
    params.get('frameBlink'),
    DEFAULT_SETTINGS.frameBlink
  );
  settings.probeMode = parseBool(
    params.get('probeMode'),
    DEFAULT_SETTINGS.probeMode
  );
  settings.probeIntervalMs = parseNumber(
    params.get('probeIntervalMs'),
    DEFAULT_SETTINGS.probeIntervalMs
  );
  settings.posX = parseNumber(params.get('posX'), DEFAULT_SETTINGS.posX);
  settings.posY = parseNumber(params.get('posY'), DEFAULT_SETTINGS.posY);
  settings.anchor = parseEnum(
    params.get('anchor'),
    Object.keys(ANCHOR_TRANSLATE),
    DEFAULT_SETTINGS.anchor
  );
  settings.safePadding = parseNumber(
    params.get('safePadding'),
    DEFAULT_SETTINGS.safePadding
  );
  settings.bgColor = params.get('bgColor') || DEFAULT_SETTINGS.bgColor;
  settings.bgOpacity = clampNumber(
    parseNumber(params.get('bgOpacity'), DEFAULT_SETTINGS.bgOpacity),
    0,
    1,
    DEFAULT_SETTINGS.bgOpacity
  );
  settings.bgPadding = parseNumber(
    params.get('bgPadding'),
    DEFAULT_SETTINGS.bgPadding
  );
  settings.bgRadius = parseNumber(
    params.get('bgRadius'),
    DEFAULT_SETTINGS.bgRadius
  );
  settings.fontFamily = params.get('fontFamily') || DEFAULT_SETTINGS.fontFamily;
  settings.fontWeight = params.get('fontWeight') || DEFAULT_SETTINGS.fontWeight;
  const scaleFactorParsed = parseNumber(
    params.get('scaleFactor'),
    DEFAULT_SETTINGS.scaleFactor
  );
  settings.scaleFactor =
    scaleFactorParsed > 0 ? scaleFactorParsed : DEFAULT_SETTINGS.scaleFactor;
  settings.showTime = parseBool(
    params.get('showTime'),
    DEFAULT_SETTINGS.showTime
  );
  settings.showFrames = parseBool(
    params.get('showFrames'),
    DEFAULT_SETTINGS.showFrames
  );
  settings.showMs = parseBool(params.get('showMs'), DEFAULT_SETTINGS.showMs);
  settings.showDiagnostics = parseBool(
    params.get('showDiagnostics'),
    DEFAULT_SETTINGS.showDiagnostics
  );
  settings.showDropFrameIndicator = parseBool(
    params.get('showDropFrameIndicator'),
    DEFAULT_SETTINGS.showDropFrameIndicator
  );
  settings.freeze = parseBool(params.get('freeze'), DEFAULT_SETTINGS.freeze);
  settings.holdOnClick = parseBool(
    params.get('holdOnClick'),
    DEFAULT_SETTINGS.holdOnClick
  );
  settings.showUI = parseBool(params.get('showUI'), DEFAULT_SETTINGS.showUI);

  return settings;
}

function updateUrlFromSettings(settings) {
  const params = new URLSearchParams();
  params.set('frameRate', settings.frameRate);
  params.set('dropFrame', settings.dropFrame);
  params.set('refreshRateMode', settings.refreshRateMode || 'auto');
  if (settings.refreshRateMode !== 'auto' && settings.refreshRate != null) {
    params.set('refreshRate', settings.refreshRate);
  }
  params.set('textColor', settings.textColor);
  params.set('borderColor', settings.borderColor);
  params.set('borderThickness', settings.borderThickness);
  params.set('timeSource', settings.timeSource);
  params.set('timeApiUrl', settings.timeApiUrl);
  params.set('syncMode', settings.syncMode);
  params.set('syncIntervalSec', settings.syncIntervalSec);
  params.set('syncSamples', settings.syncSamples);
  params.set('syncSmoothing', settings.syncSmoothing);
  if (settings.timeSource === 'fixed' && settings.fixedStart) {
    params.set('fixedStart', settings.fixedStart);
  }
  params.set('tcOffsetMs', settings.tcOffsetMs);
  params.set('tcOffsetFrames', settings.tcOffsetFrames);
  params.set('markerInterval', settings.markerInterval);
  params.set('markerDurationMs', settings.markerDurationMs);
  params.set('markerColor', settings.markerColor);
  params.set('frameBlink', settings.frameBlink);
  params.set('probeMode', settings.probeMode);
  params.set('probeIntervalMs', settings.probeIntervalMs);
  params.set('posX', settings.posX);
  params.set('posY', settings.posY);
  params.set('anchor', settings.anchor);
  params.set('safePadding', settings.safePadding);
  params.set('bgColor', settings.bgColor);
  params.set('bgOpacity', settings.bgOpacity);
  params.set('bgPadding', settings.bgPadding);
  params.set('bgRadius', settings.bgRadius);
  params.set('fontFamily', settings.fontFamily);
  params.set('fontWeight', settings.fontWeight);
  params.set('scaleFactor', settings.scaleFactor);
  params.set('showTime', settings.showTime);
  params.set('showFrames', settings.showFrames);
  params.set('showMs', settings.showMs);
  params.set('showDiagnostics', settings.showDiagnostics);
  params.set('showDropFrameIndicator', settings.showDropFrameIndicator);
  params.set('freeze', settings.freeze);
  params.set('holdOnClick', settings.holdOnClick);
  params.set('showUI', settings.showUI);

  const query = params.toString();
  const newUrl = query
    ? `${window.location.pathname}?${query}${window.location.hash}`
    : `${window.location.pathname}${window.location.hash}`;
  window.history.replaceState(null, '', newUrl);
}

function App() {
  const [settings, setSettings] = useState(() => parseSettingsFromUrl());
  const [statusMessage, setStatusMessage] = useState('');
  const [baseTime, setBaseTime] = useState(null);
  const [detectedRefreshRate, setDetectedRefreshRate] = useState(null);
  const [frameStats, setFrameStats] = useState({
    min: null,
    max: null,
    avg: null,
    fps: null,
    rafAvg: null,
    rafFps: null,
  });
  const [networkStatus, setNetworkStatus] = useState({
    status: 'idle',
    driftMs: null,
    offsetMs: null,
    rttMs: null,
    rttMinMs: null,
    rttMaxMs: null,
    rttAvgMs: null,
    offsetJitterMs: null,
  });
  const [uiOpen, setUiOpen] = useState(true);

  const settingsRef = useRef(settings);
  const requestRef = useRef(null);
  const lastRafRef = useRef(null);
  const accumulatorRef = useRef(0);
  const basePerfRef = useRef(null);
  const baseTimeRef = useRef(null);
  const lastFrameTimeRef = useRef(null);
  const frozenTimeRef = useRef(null);
  const currentTimeMsRef = useRef(null);
  const rafStatsRef = useRef({
    min: Infinity,
    max: 0,
    sum: 0,
    count: 0,
    lastReport: performance.now(),
  });
  const statsRef = useRef({
    min: Infinity,
    max: 0,
    sum: 0,
    count: 0,
    lastReport: performance.now(),
  });

  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const hiddenMeasureRef = useRef(null);
  const timecodeValueRef = useRef(null);
  const msValueRef = useRef(null);
  const markerRef = useRef(null);
  const frameBlinkRef = useRef(null);

  const effectiveRefreshRate =
    settings.refreshRateMode === 'auto'
      ? detectedRefreshRate
      : settings.refreshRate ?? detectedRefreshRate;
  const dropFrameEffective =
    settings.dropFrame && VALID_DROP_FRAME_RATES.includes(settings.frameRate);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    baseTimeRef.current = baseTime;
  }, [baseTime]);

  useEffect(() => {
    updateUrlFromSettings(settings);
  }, [settings]);

  useEffect(() => {
    if (
      settings.dropFrame &&
      !VALID_DROP_FRAME_RATES.includes(settings.frameRate)
    ) {
      setStatusMessage(
        'Drop-frame not supported for this frame rate. Using non-drop-frame.'
      );
      setSettings((prev) => ({ ...prev, dropFrame: false }));
    }
  }, [settings.dropFrame, settings.frameRate]);

  useEffect(() => {
    let cancelled = false;
    let intervalId = null;

    const applySyncedOffset = (offsetMs, rttMs) => {
      const nowPerf = performance.now();
      const nowClient = Date.now();
      const currentDisplayed =
        baseTimeRef.current != null && basePerfRef.current != null
          ? baseTimeRef.current + (nowPerf - basePerfRef.current)
          : null;
      let targetBaseTime = nowClient + offsetMs;
      if (currentDisplayed != null) {
        const diff = targetBaseTime - currentDisplayed;
        if (Math.abs(diff) < 2000) {
          const smoothing = clampNumber(
            parseNumber(settings.syncSmoothing, DEFAULT_SETTINGS.syncSmoothing),
            0,
            1,
            DEFAULT_SETTINGS.syncSmoothing
          );
          targetBaseTime = currentDisplayed + diff * smoothing;
        }
      }
      basePerfRef.current = nowPerf;
      baseTimeRef.current = targetBaseTime;
      setBaseTime(targetBaseTime);
      setNetworkStatus({
        status: 'ok',
        driftMs: offsetMs,
        offsetMs,
        rttMs,
        rttMinMs: null,
        rttMaxMs: null,
        rttAvgMs: null,
        offsetJitterMs: null,
      });
      setStatusMessage('');
    };

    const handleUtcError = () => {
      setStatusMessage('Error fetching UTC time, using local time.');
      basePerfRef.current = performance.now();
      setBaseTime(Date.now());
      setNetworkStatus({
        status: 'error',
        driftMs: null,
        offsetMs: null,
        rttMs: null,
        rttMinMs: null,
        rttMaxMs: null,
        rttAvgMs: null,
        offsetJitterMs: null,
      });
    };

    const syncUtcTime = async () => {
      setStatusMessage('Syncing UTC time...');
      setNetworkStatus({
        status: 'pending',
        driftMs: null,
        offsetMs: null,
        rttMs: null,
        rttMinMs: null,
        rttMaxMs: null,
        rttAvgMs: null,
        offsetJitterMs: null,
      });
      const apiUrl = settings.timeApiUrl || DEFAULT_SETTINGS.timeApiUrl;
      const sampleCount = Math.max(
        1,
        Math.min(10, Math.round(settings.syncSamples || 1))
      );
      const samples = [];
      for (let i = 0; i < sampleCount; i += 1) {
        if (cancelled) return;
        try {
          const sample = await fetchServerTimeSample(apiUrl);
          samples.push(sample);
        } catch (error) {
          // Continue sampling; fallback if all fail.
        }
      }
      if (!samples.length) {
        if (!cancelled) handleUtcError();
        return;
      }
      const sortedByRtt = [...samples].sort((a, b) => a.rttMs - b.rttMs);
      const subsetSize = Math.max(1, Math.ceil(sortedByRtt.length / 2));
      const bestSubset = sortedByRtt.slice(0, subsetSize);
      const offsets = bestSubset
        .map(
          (sample) => sample.serverTimeMs + sample.rttMs / 2 - sample.clientNowMs
        )
        .sort((a, b) => a - b);
      const medianOffset =
        offsets.length % 2 === 1
          ? offsets[(offsets.length - 1) / 2]
          : (offsets[offsets.length / 2 - 1] + offsets[offsets.length / 2]) / 2;
      const rttValues = bestSubset.map((sample) => sample.rttMs);
      const rttMinMs = Math.min(...rttValues);
      const rttMaxMs = Math.max(...rttValues);
      const rttAvgMs =
        rttValues.reduce((sum, value) => sum + value, 0) / rttValues.length;
      const offsetJitterMs =
        offsets.length > 1 ? offsets[offsets.length - 1] - offsets[0] : 0;

      applySyncedOffset(medianOffset, rttMinMs);
      setNetworkStatus((prev) => ({
        ...prev,
        rttMinMs,
        rttMaxMs,
        rttAvgMs,
        offsetJitterMs,
      }));
    };

    if (settings.timeSource === 'utc') {
      syncUtcTime();
      if (settings.syncMode === 'periodic') {
        const intervalMs = Math.max(
          5,
          parseNumber(settings.syncIntervalSec, DEFAULT_SETTINGS.syncIntervalSec)
        ) * 1000;
        intervalId = setInterval(syncUtcTime, intervalMs);
      }
      return () => {
        cancelled = true;
        if (intervalId) clearInterval(intervalId);
      };
    }

    if (settings.timeSource === 'local') {
      basePerfRef.current = performance.now();
      setBaseTime(Date.now());
      setNetworkStatus({
        status: 'local',
        driftMs: 0,
        offsetMs: 0,
        rttMs: null,
        rttMinMs: null,
        rttMaxMs: null,
        rttAvgMs: null,
        offsetJitterMs: null,
      });
      setStatusMessage('');
      return () => {
        cancelled = true;
        if (intervalId) clearInterval(intervalId);
      };
    }

    const fixedTime = parseFixedStart(settings.fixedStart);
    basePerfRef.current = performance.now();
    setBaseTime(fixedTime);
    setNetworkStatus({
      status: 'fixed',
      driftMs: 0,
      offsetMs: 0,
      rttMs: null,
      rttMinMs: null,
      rttMaxMs: null,
      rttAvgMs: null,
      offsetJitterMs: null,
    });
    setStatusMessage('');
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [
    settings.timeSource,
    settings.fixedStart,
    settings.timeApiUrl,
    settings.syncMode,
    settings.syncIntervalSec,
    settings.syncSamples,
    settings.syncSmoothing,
  ]);

  useEffect(() => {
    let cancelled = false;
    if (settings.refreshRateMode === 'auto') {
      detectScreenRefreshRate().then((detectedFrameTime) => {
        if (cancelled) return;
        const fps = 1000 / detectedFrameTime;
        setDetectedRefreshRate(fps);
      });
    } else {
      setDetectedRefreshRate(null);
    }
    return () => {
      cancelled = true;
    };
  }, [settings.refreshRateMode]);

  useEffect(() => {
    if (baseTime == null || effectiveRefreshRate == null) return;

    lastRafRef.current = null;
    accumulatorRef.current = 0;
    lastFrameTimeRef.current = null;
    frozenTimeRef.current = null;
    currentTimeMsRef.current = null;
    rafStatsRef.current = {
      min: Infinity,
      max: 0,
      sum: 0,
      count: 0,
      lastReport: performance.now(),
    };
    statsRef.current = {
      min: Infinity,
      max: 0,
      sum: 0,
      count: 0,
      lastReport: performance.now(),
    };

    const animate = () => {
      requestRef.current = requestAnimationFrame(animate);
      if (!baseTime || effectiveRefreshRate == null || basePerfRef.current == null)
        return;

      const now = performance.now();
      const frameDuration =
        effectiveRefreshRate > 0 ? 1000 / effectiveRefreshRate : 0;
      if (!frameDuration) return;
      if (lastRafRef.current == null) {
        lastRafRef.current = now;
        return;
      }
      const delta = now - lastRafRef.current;
      lastRafRef.current = now;
      const rafStats = rafStatsRef.current;
      rafStats.min = Math.min(rafStats.min, delta);
      rafStats.max = Math.max(rafStats.max, delta);
      rafStats.sum += delta;
      rafStats.count += 1;
      let rafReport = null;
      if (now - rafStats.lastReport >= 1000) {
        const rafAvg = rafStats.count ? rafStats.sum / rafStats.count : null;
        rafReport = {
          rafAvg,
          rafFps: rafAvg ? 1000 / rafAvg : null,
        };
        rafStatsRef.current = {
          min: Infinity,
          max: 0,
          sum: 0,
          count: 0,
          lastReport: now,
        };
      }
      accumulatorRef.current += delta;
      const shouldUpdate = accumulatorRef.current >= frameDuration;
      if (accumulatorRef.current > frameDuration * 2) {
        accumulatorRef.current = frameDuration;
      } else if (shouldUpdate) {
        accumulatorRef.current -= frameDuration;
      }
      if (!shouldUpdate) {
        if (rafReport) {
          setFrameStats((prev) => ({ ...prev, ...rafReport }));
        }
        return;
      }

      const currentSettings = settingsRef.current;
      const liveTimeMs = baseTime + (now - basePerfRef.current);
      const offsetMs =
        currentSettings.tcOffsetMs +
        (currentSettings.tcOffsetFrames / currentSettings.frameRate) * 1000;
      let displayTimeMs = liveTimeMs + offsetMs;

      if (currentSettings.freeze) {
        if (frozenTimeRef.current == null) {
          frozenTimeRef.current = displayTimeMs;
        }
        displayTimeMs = frozenTimeRef.current;
      } else {
        frozenTimeRef.current = null;
      }

      currentTimeMsRef.current = displayTimeMs;

      const dropFrameActive =
        currentSettings.dropFrame &&
        VALID_DROP_FRAME_RATES.includes(currentSettings.frameRate);
      const tc = new Timecode(
        new Date(displayTimeMs),
        currentSettings.frameRate,
        dropFrameActive
      );
      const tcString = tc.toString();
      const timecodeText = formatTimecode(
        tcString,
        currentSettings.showTime,
        currentSettings.showFrames
      );
      if (timecodeValueRef.current) {
        if (timecodeValueRef.current.textContent !== timecodeText) {
          timecodeValueRef.current.textContent = timecodeText;
        }
      }

      if (msValueRef.current) {
        const msText = `${new Date(displayTimeMs)
          .getMilliseconds()
          .toString()
          .padStart(3, '0')} ms`;
        if (msValueRef.current.textContent !== msText) {
          msValueRef.current.textContent = msText;
        }
      }

      const markerIntervalMs =
        currentSettings.markerInterval === 'off'
          ? 0
          : parseNumber(currentSettings.markerInterval, 0) * 1000;
      if (markerRef.current) {
        const markerActive =
          markerIntervalMs > 0 &&
          displayTimeMs % markerIntervalMs < currentSettings.markerDurationMs;
        markerRef.current.classList.toggle('is-active', markerActive);
      }

      if (frameBlinkRef.current) {
        const frameIndex = Math.floor(
          (displayTimeMs / 1000) * currentSettings.frameRate
        );
        const frameBlinkOn =
          currentSettings.frameBlink && frameIndex % 2 === 0;
        frameBlinkRef.current.classList.toggle('is-on', frameBlinkOn);
      }

      if (containerRef.current) {
        if (currentSettings.probeMode) {
          const probeInterval = Math.max(1, currentSettings.probeIntervalMs || 1);
          const probeActive =
            Math.floor(displayTimeMs / probeInterval) % 2 === 0;
          const probeColor = probeActive ? '#000' : '#fff';
          if (containerRef.current.style.backgroundColor !== probeColor) {
            containerRef.current.style.backgroundColor = probeColor;
          }
        } else if (containerRef.current.style.backgroundColor !== 'transparent') {
          containerRef.current.style.backgroundColor = 'transparent';
        }
      }

      const lastFrameTime = lastFrameTimeRef.current;
      let updateReport = null;
      if (lastFrameTime != null) {
        const dt = now - lastFrameTime;
        const stats = statsRef.current;
        stats.min = Math.min(stats.min, dt);
        stats.max = Math.max(stats.max, dt);
        stats.sum += dt;
        stats.count += 1;

        if (now - stats.lastReport >= 1000) {
          const avg = stats.count ? stats.sum / stats.count : null;
          updateReport = {
            min: stats.count ? stats.min : null,
            max: stats.count ? stats.max : null,
            avg,
            fps: avg ? 1000 / avg : null,
          };
          statsRef.current = {
            min: Infinity,
            max: 0,
            sum: 0,
            count: 0,
            lastReport: now,
          };
        }
      }
      lastFrameTimeRef.current = now;

      if (updateReport || rafReport) {
        setFrameStats((prev) => ({
          ...prev,
          ...(updateReport || {}),
          ...(rafReport || {}),
        }));
      }
    };

    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current);
  }, [baseTime, effectiveRefreshRate]);
  const indicatorLabel = settings.showDropFrameIndicator
    ? dropFrameEffective
      ? 'DF'
      : 'NDF'
    : '';
  const frameDigits = Math.max(
    2,
    String(Math.round(settings.frameRate || 0)).length
  );
  const frameSample = '8'.repeat(frameDigits);
  const timeSample = '88:88:88';
  const timecodeMeasure =
    settings.showTime && settings.showFrames
      ? `${timeSample};${frameSample}`
      : settings.showTime
        ? timeSample
        : settings.showFrames
          ? frameSample
          : '';
  const msMeasure = settings.showMs ? '888 ms' : '';
  const framePlaceholder = '-'.repeat(frameDigits);
  const timecodePlaceholder =
    settings.showTime && settings.showFrames
      ? `--:--:--;${framePlaceholder}`
      : settings.showTime
        ? '--:--:--'
        : settings.showFrames
          ? framePlaceholder
          : '';

  useLayoutEffect(() => {
    if (!containerRef.current || !hiddenMeasureRef.current) return;

    const safePadding = Math.max(0, settings.safePadding || 0);
    const containerWidth =
      containerRef.current.clientWidth - safePadding * 2;
    const containerHeight =
      containerRef.current.clientHeight - safePadding * 2;

    const measuredWidth = hiddenMeasureRef.current.scrollWidth;
    const measuredHeight = hiddenMeasureRef.current.scrollHeight;

    if (containerWidth <= 0 || containerHeight <= 0) return;
    if (measuredWidth <= 0 || measuredHeight <= 0) return;

    const widthScale = containerWidth / measuredWidth;
    const heightScale = containerHeight / measuredHeight;
    const finalScale = Math.min(widthScale, heightScale);

    setScale(finalScale);
  }, [
    settings.frameRate,
    settings.showTime,
    settings.showFrames,
    settings.showMs,
    settings.bgPadding,
    settings.fontFamily,
    settings.fontWeight,
    settings.safePadding,
    settings.borderThickness,
    settings.showDropFrameIndicator,
    dropFrameEffective,
  ]);

  const outlineThickness = Math.max(
    0,
    parseFloat(settings.borderThickness) || 0
  );
  const outlineColor = settings.borderColor;
  const outlineShadow =
    outlineThickness > 0
      ? [
          `${outlineThickness}px 0 0 ${outlineColor}`,
          `-${outlineThickness}px 0 0 ${outlineColor}`,
          `0 ${outlineThickness}px 0 ${outlineColor}`,
          `0 -${outlineThickness}px 0 ${outlineColor}`,
          `${outlineThickness}px ${outlineThickness}px 0 ${outlineColor}`,
          `-${outlineThickness}px ${outlineThickness}px 0 ${outlineColor}`,
          `${outlineThickness}px -${outlineThickness}px 0 ${outlineColor}`,
          `-${outlineThickness}px -${outlineThickness}px 0 ${outlineColor}`,
        ].join(', ')
      : 'none';

  const outlineStyle =
    outlineThickness > 0
      ? {
          WebkitTextStroke: `${outlineThickness}px ${outlineColor}`,
          textShadow: outlineShadow,
        }
      : {};

  const scaleFactor =
    settings.scaleFactor > 0 ? settings.scaleFactor : DEFAULT_SETTINGS.scaleFactor;
  const displayScale = scale * scaleFactor;

  const bgColor = applyOpacity(settings.bgColor, settings.bgOpacity);
  const measurePadding =
    Math.max(0, settings.bgPadding || 0) + outlineThickness * 2;
  const [translateX, translateY] =
    ANCHOR_TRANSLATE[settings.anchor] || ANCHOR_TRANSLATE.center;
  const posX = clampNumber(settings.posX, 0, 100, 50);
  const posY = clampNumber(settings.posY, 0, 100, 50);

  const textColorPicker = normalizeHexColor(settings.textColor, '#eeeeee');
  const outlineColorPicker = normalizeHexColor(settings.borderColor, '#000000');
  const markerColorPicker = normalizeHexColor(settings.markerColor, '#ff0000');
  const bgColorPicker = normalizeHexColor(settings.bgColor, '#000000');

  const fontFamilySelectValue = FONT_OPTIONS.some(
    (option) => option.value === settings.fontFamily
  )
    ? settings.fontFamily
    : 'custom';
  const fontWeightSelectValue = FONT_WEIGHT_OPTIONS.some(
    (option) => option.value === settings.fontWeight
  )
    ? settings.fontWeight
    : 'custom';

  const renderFpsWarning =
    settings.showDiagnostics &&
    effectiveRefreshRate &&
    frameStats.rafFps &&
    frameStats.rafFps < effectiveRefreshRate * 0.9;

  const showUI = settings.showUI !== false;

  const updateSetting = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleNumber = (key, fallback = 0) => (event) => {
    const next = parseNumber(event.target.value, fallback);
    updateSetting(key, next);
  };

  const handleFrameRate = (event) => {
    const next = parseNumber(event.target.value, DEFAULT_SETTINGS.frameRate);
    updateSetting('frameRate', next > 0 ? next : DEFAULT_SETTINGS.frameRate);
  };

  const handleText = (key) => (event) => {
    updateSetting(key, event.target.value);
  };

  const handleBool = (key) => (event) => {
    updateSetting(key, event.target.checked);
  };

  const handleSelect = (key) => (event) => {
    updateSetting(key, event.target.value);
  };

  const handleRefreshMode = (event) => {
    const next = event.target.value;
    if (next === 'auto') {
      updateSetting('refreshRateMode', 'auto');
      return;
    }
    const rate = parseNumber(next, null);
    if (rate != null && rate > 0) {
      updateSetting('refreshRateMode', 'manual');
      updateSetting('refreshRate', rate);
    }
  };

  const handleHoldClick = () => {
    if (!settings.holdOnClick) return;
    updateSetting('freeze', !settings.freeze);
  };

  return (
    <div
      className="container"
      ref={containerRef}
      style={{
        padding: `${Math.max(0, settings.safePadding || 0)}px`,
        backgroundColor: 'transparent',
      }}
    >
      <div
        className="text-wrapper"
        style={{
          transform: `translate(${translateX}, ${translateY}) scale(${displayScale})`,
          left: `${posX}%`,
          top: `${posY}%`,
          cursor: settings.holdOnClick ? 'pointer' : 'default',
        }}
        onClick={handleHoldClick}
      >
        <div
          className="text-content"
          style={{
            backgroundColor: bgColor,
            padding: `${Math.max(0, settings.bgPadding || 0)}px`,
            borderRadius: `${Math.max(0, settings.bgRadius || 0)}px`,
            fontFamily: settings.fontFamily,
            fontWeight: settings.fontWeight,
          }}
        >
          {(settings.showTime || settings.showFrames) && (
            <div
              className="timecode-display"
              style={{ color: settings.textColor, ...outlineStyle }}
            >
              <span ref={timecodeValueRef}>{timecodePlaceholder}</span>
              {settings.showDropFrameIndicator && (
                <span className="dropframe-indicator">
                  {dropFrameEffective ? 'DF' : 'NDF'}
                </span>
              )}
            </div>
          )}
          {settings.showMs && (
            <div
              className="millisecond-display"
              style={{ color: settings.textColor, ...outlineStyle }}
            >
              <span ref={msValueRef}>000 ms</span>
            </div>
          )}
        </div>
      </div>

      {settings.markerInterval !== 'off' && (
        <div
          ref={markerRef}
          className="marker-flash"
          style={{ backgroundColor: settings.markerColor }}
        />
      )}

      {settings.frameBlink && (
        <div
          ref={frameBlinkRef}
          className="frame-blink"
          style={{ backgroundColor: settings.textColor }}
        />
      )}

      <div id="status">{statusMessage}</div>

      {settings.showDiagnostics && (
        <div className="diagnostics">
          <div>Target rate: {effectiveRefreshRate?.toFixed(2) || 'n/a'} Hz</div>
          <div>
            Update time: {frameStats.avg ? frameStats.avg.toFixed(2) : 'n/a'} ms
            (min {frameStats.min ? frameStats.min.toFixed(2) : 'n/a'} / max{' '}
            {frameStats.max ? frameStats.max.toFixed(2) : 'n/a'})
          </div>
          <div>
            Update FPS: {frameStats.fps ? frameStats.fps.toFixed(2) : 'n/a'}
          </div>
          <div>
            Render FPS: {frameStats.rafFps ? frameStats.rafFps.toFixed(2) : 'n/a'}
          </div>
          {renderFpsWarning && (
            <div>
              Warning: Render FPS is below target. This is limited by the
              display refresh rate or browser throttling.
            </div>
          )}
          <div>
            Time source: {settings.timeSource}
            {networkStatus.status === 'ok' && networkStatus.driftMs != null
              ? ` (drift ${networkStatus.driftMs.toFixed(0)} ms)`
              : ''}
            {networkStatus.status === 'error' ? ' (UTC fetch failed)' : ''}
          </div>
          <div>
            Sync offset:{' '}
            {networkStatus.offsetMs != null
              ? `${networkStatus.offsetMs.toFixed(0)} ms`
              : 'n/a'}
            {' | '}RTT:{' '}
            {networkStatus.rttMs != null
              ? `${networkStatus.rttMs.toFixed(0)} ms`
              : 'n/a'}
          </div>
          <div>
            Sync quality:{' '}
            {networkStatus.offsetJitterMs != null
              ? `${networkStatus.offsetJitterMs.toFixed(0)} ms jitter`
              : 'n/a'}
            {' | '}RTT avg:{' '}
            {networkStatus.rttAvgMs != null
              ? `${networkStatus.rttAvgMs.toFixed(0)} ms`
              : 'n/a'}
            {' | '}RTT range:{' '}
            {networkStatus.rttMinMs != null && networkStatus.rttMaxMs != null
              ? `${networkStatus.rttMinMs.toFixed(0)}-${networkStatus.rttMaxMs.toFixed(0)} ms`
              : 'n/a'}
          </div>
        </div>
      )}

      {showUI && (
        <>
          <button
            className="settings-toggle"
            type="button"
            onClick={() => setUiOpen((open) => !open)}
            aria-label="Toggle settings"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 5c-5 0-9 4.5-10 7 1 2.5 5 7 10 7s9-4.5 10-7c-1-2.5-5-7-10-7zm0 11a4 4 0 1 1 0-8 4 4 0 0 1 0 8z" />
              <circle cx="12" cy="12" r="2.5" />
            </svg>
          </button>

          {uiOpen && (
            <div className="settings-panel">
              <div className="settings-group">
                <div className="settings-title">Time</div>
                <label className="settings-row">
                  <span className="settings-label">
                    Time source
                    <HelpIcon text="Select timebase: UTC via server, local clock, or fixed start." />
                  </span>
                  <select
                    value={settings.timeSource}
                    onChange={handleSelect('timeSource')}
                  >
                    <option value="utc">UTC (server)</option>
                    <option value="local">Local clock</option>
                    <option value="fixed">Fixed start</option>
                  </select>
                </label>
                {settings.timeSource === 'utc' && (
                  <>
                    <label className="settings-row">
                      <span className="settings-label">
                        Time API URL
                        <HelpIcon text="UTC time endpoint returning iso8601 or timestamp." />
                      </span>
                      <input
                        type="text"
                        value={settings.timeApiUrl}
                        onChange={handleText('timeApiUrl')}
                      />
                    </label>
                    <label className="settings-row">
                      <span className="settings-label">
                        Sync mode
                        <HelpIcon text="Single sync on load or periodic resync." />
                      </span>
                      <select
                        value={settings.syncMode}
                        onChange={handleSelect('syncMode')}
                      >
                        <option value="single">Single</option>
                        <option value="periodic">Periodic</option>
                      </select>
                    </label>
                    <label className="settings-row">
                      <span className="settings-label">
                        Sync samples
                        <HelpIcon text="Number of samples per sync; higher reduces jitter." />
                      </span>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        step="1"
                        value={settings.syncSamples}
                        onChange={handleNumber('syncSamples', 3)}
                      />
                    </label>
                    <label className="settings-row">
                      <span className="settings-label">
                        Sync smoothing
                        <HelpIcon text="0-1: how strongly to apply sync correction. 1 = hard snap." />
                      </span>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.05"
                        value={settings.syncSmoothing}
                        onChange={handleNumber('syncSmoothing', 0.2)}
                      />
                    </label>
                    {settings.syncMode === 'periodic' && (
                      <label className="settings-row">
                        <span className="settings-label">
                          Sync interval (sec)
                          <HelpIcon text="Seconds between periodic syncs." />
                        </span>
                        <input
                          type="number"
                          min="5"
                          step="1"
                          value={settings.syncIntervalSec}
                          onChange={handleNumber('syncIntervalSec', 30)}
                        />
                      </label>
                    )}
                  </>
                )}
                {settings.timeSource === 'fixed' && (
                  <label className="settings-row">
                    <span className="settings-label">
                      Fixed start
                      <HelpIcon text="ISO string or epoch ms for fixed timebase." />
                    </span>
                    <input
                      type="text"
                      value={settings.fixedStart}
                      onChange={handleText('fixedStart')}
                      placeholder="ISO or epoch ms"
                    />
                  </label>
                )}
                <label className="settings-row">
                  <span className="settings-label">
                    Frame rate
                    <HelpIcon text="Timecode frame rate (fps)." />
                  </span>
                  <input
                    type="number"
                    step="0.001"
                    value={settings.frameRate}
                    onChange={handleFrameRate}
                  />
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Drop-frame
                    <HelpIcon text="Use drop-frame timecode (23.976/29.97/59.94)." />
                  </span>
                  <input
                    type="checkbox"
                    checked={settings.dropFrame}
                    onChange={handleBool('dropFrame')}
                  />
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    DF/NDF indicator
                    <HelpIcon text="Show DF/NDF label next to timecode." />
                  </span>
                  <input
                    type="checkbox"
                    checked={settings.showDropFrameIndicator}
                    onChange={handleBool('showDropFrameIndicator')}
                  />
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Refresh rate
                    <HelpIcon text="Target update rate; auto uses display refresh." />
                  </span>
                  <select
                    value={
                      settings.refreshRateMode === 'auto'
                        ? 'auto'
                        : String(settings.refreshRate ?? 'auto')
                    }
                    onChange={handleRefreshMode}
                  >
                    <option value="auto">Auto (current display)</option>
                    {REFRESH_RATE_OPTIONS.map((rate) => (
                      <option key={rate} value={String(rate)}>
                        {rate} Hz
                      </option>
                    ))}
                  </select>
                </label>
                {settings.refreshRateMode !== 'auto' && (
                  <label className="settings-row">
                    <span className="settings-label">
                      Manual rate
                      <HelpIcon text="Manual target refresh rate (Hz)." />
                    </span>
                    <input
                      type="number"
                      step="0.1"
                      min="1"
                      value={settings.refreshRate ?? ''}
                      onChange={handleNumber('refreshRate', 60)}
                    />
                  </label>
                )}
              </div>

              <div className="settings-group">
                <div className="settings-title">Offsets</div>
                <label className="settings-row">
                  <span className="settings-label">
                    Offset ms
                    <HelpIcon text="Add/subtract milliseconds to timecode." />
                  </span>
                  <input
                    type="number"
                    step="1"
                    value={settings.tcOffsetMs}
                    onChange={handleNumber('tcOffsetMs', 0)}
                  />
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Offset frames
                    <HelpIcon text="Add/subtract frames to timecode." />
                  </span>
                  <input
                    type="number"
                    step="1"
                    value={settings.tcOffsetFrames}
                    onChange={handleNumber('tcOffsetFrames', 0)}
                  />
                </label>
              </div>

              <div className="settings-group">
                <div className="settings-title">Display</div>
                <label className="settings-row">
                  <span className="settings-label">
                    Text color
                    <HelpIcon text="Color of timecode text." />
                  </span>
                  <div className="settings-inline">
                    <input
                      type="color"
                      value={textColorPicker}
                      onChange={(event) =>
                        updateSetting('textColor', event.target.value)
                      }
                      aria-label="Text color picker"
                    />
                    <input
                      type="text"
                      value={settings.textColor}
                      onChange={handleText('textColor')}
                    />
                  </div>
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Outline color
                    <HelpIcon text="Outline/stroke color." />
                  </span>
                  <div className="settings-inline">
                    <input
                      type="color"
                      value={outlineColorPicker}
                      onChange={(event) =>
                        updateSetting('borderColor', event.target.value)
                      }
                      aria-label="Outline color picker"
                    />
                    <input
                      type="text"
                      value={settings.borderColor}
                      onChange={handleText('borderColor')}
                    />
                  </div>
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Outline px
                    <HelpIcon text="Outline thickness in pixels." />
                  </span>
                  <input
                    type="number"
                    step="1"
                    value={settings.borderThickness}
                    onChange={handleNumber('borderThickness', 0)}
                  />
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Background color
                    <HelpIcon text="Background box color behind text." />
                  </span>
                  <div className="settings-inline">
                    <input
                      type="color"
                      value={bgColorPicker}
                      onChange={(event) =>
                        updateSetting('bgColor', event.target.value)
                      }
                      aria-label="Background color picker"
                    />
                    <input
                      type="text"
                      value={settings.bgColor}
                      onChange={handleText('bgColor')}
                    />
                  </div>
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Background opacity
                    <HelpIcon text="0-1 opacity for background." />
                  </span>
                  <input
                    type="number"
                    step="0.05"
                    min="0"
                    max="1"
                    value={settings.bgOpacity}
                    onChange={handleNumber('bgOpacity', 0)}
                  />
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Background padding
                    <HelpIcon text="Padding around text in pixels." />
                  </span>
                  <input
                    type="number"
                    step="1"
                    value={settings.bgPadding}
                    onChange={handleNumber('bgPadding', 0)}
                  />
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Background radius
                    <HelpIcon text="Corner radius of background in pixels." />
                  </span>
                  <input
                    type="number"
                    step="1"
                    value={settings.bgRadius}
                    onChange={handleNumber('bgRadius', 0)}
                  />
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Font family
                    <HelpIcon text="Font family or stack." />
                  </span>
                  <div className="settings-inline">
                    <select
                      value={fontFamilySelectValue}
                      onChange={(event) => {
                        const next = event.target.value;
                        if (next !== 'custom') {
                          updateSetting('fontFamily', next);
                        }
                      }}
                    >
                      {FONT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                      <option value="custom">Custom</option>
                    </select>
                    <input
                      type="text"
                      value={settings.fontFamily}
                      onChange={handleText('fontFamily')}
                      placeholder="Custom font stack"
                    />
                  </div>
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Font weight
                    <HelpIcon text="Font weight or keyword." />
                  </span>
                  <div className="settings-inline">
                    <select
                      value={fontWeightSelectValue}
                      onChange={(event) => {
                        const next = event.target.value;
                        if (next !== 'custom') {
                          updateSetting('fontWeight', next);
                        }
                      }}
                    >
                      {FONT_WEIGHT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                      <option value="custom">Custom</option>
                    </select>
                    <input
                      type="text"
                      value={settings.fontWeight}
                      onChange={handleText('fontWeight')}
                      placeholder="Custom weight"
                    />
                  </div>
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Show time
                    <HelpIcon text="Show HH:MM:SS." />
                  </span>
                  <input
                    type="checkbox"
                    checked={settings.showTime}
                    onChange={handleBool('showTime')}
                  />
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Show frames
                    <HelpIcon text="Show frame count." />
                  </span>
                  <input
                    type="checkbox"
                    checked={settings.showFrames}
                    onChange={handleBool('showFrames')}
                  />
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Show ms
                    <HelpIcon text="Show milliseconds row." />
                  </span>
                  <input
                    type="checkbox"
                    checked={settings.showMs}
                    onChange={handleBool('showMs')}
                  />
                </label>
              </div>

              <div className="settings-group">
                <div className="settings-title">Markers & Modes</div>
                <label className="settings-row">
                  <span className="settings-label">
                    Marker interval
                    <HelpIcon text="Flash overlay every N seconds." />
                  </span>
                  <select
                    value={settings.markerInterval}
                    onChange={handleSelect('markerInterval')}
                  >
                    <option value="off">Off</option>
                    <option value="1">1s</option>
                    <option value="5">5s</option>
                    <option value="60">60s</option>
                  </select>
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Marker duration
                    <HelpIcon text="Flash duration in milliseconds." />
                  </span>
                  <input
                    type="number"
                    step="10"
                    value={settings.markerDurationMs}
                    onChange={handleNumber('markerDurationMs', 120)}
                  />
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Marker color
                    <HelpIcon text="Flash color." />
                  </span>
                  <div className="settings-inline">
                    <input
                      type="color"
                      value={markerColorPicker}
                      onChange={(event) =>
                        updateSetting('markerColor', event.target.value)
                      }
                      aria-label="Marker color picker"
                    />
                    <input
                      type="text"
                      value={settings.markerColor}
                      onChange={handleText('markerColor')}
                    />
                  </div>
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Frame blink
                    <HelpIcon text="Blink dot every frame to spot stutter." />
                  </span>
                  <input
                    type="checkbox"
                    checked={settings.frameBlink}
                    onChange={handleBool('frameBlink')}
                  />
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Probe mode
                    <HelpIcon text="Alternate black/white background for latency tests." />
                  </span>
                  <input
                    type="checkbox"
                    checked={settings.probeMode}
                    onChange={handleBool('probeMode')}
                  />
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Probe interval
                    <HelpIcon text="Probe toggle interval in milliseconds." />
                  </span>
                  <input
                    type="number"
                    step="50"
                    value={settings.probeIntervalMs}
                    onChange={handleNumber('probeIntervalMs', 1000)}
                  />
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Freeze
                    <HelpIcon text="Pause timecode at current time." />
                  </span>
                  <input
                    type="checkbox"
                    checked={settings.freeze}
                    onChange={handleBool('freeze')}
                  />
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Hold on click
                    <HelpIcon text="Click timecode to toggle freeze." />
                  </span>
                  <input
                    type="checkbox"
                    checked={settings.holdOnClick}
                    onChange={handleBool('holdOnClick')}
                  />
                </label>
              </div>

              <div className="settings-group">
                <div className="settings-title">Layout</div>
                <label className="settings-row">
                  <span className="settings-label">
                    Pos X (%)
                    <HelpIcon text="Horizontal position as % of viewport." />
                  </span>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    value={settings.posX}
                    onChange={handleNumber('posX', 50)}
                  />
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Pos Y (%)
                    <HelpIcon text="Vertical position as % of viewport." />
                  </span>
                  <input
                    type="number"
                    step="1"
                    min="0"
                    max="100"
                    value={settings.posY}
                    onChange={handleNumber('posY', 50)}
                  />
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Anchor
                    <HelpIcon text="Anchor point for positioning." />
                  </span>
                  <select
                    value={settings.anchor}
                    onChange={handleSelect('anchor')}
                  >
                    <option value="center">Center</option>
                    <option value="top-left">Top left</option>
                    <option value="top">Top</option>
                    <option value="top-right">Top right</option>
                    <option value="left">Left</option>
                    <option value="right">Right</option>
                    <option value="bottom-left">Bottom left</option>
                    <option value="bottom">Bottom</option>
                    <option value="bottom-right">Bottom right</option>
                  </select>
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Safe padding
                    <HelpIcon text="Keep text away from edges in pixels." />
                  </span>
                  <input
                    type="number"
                    step="1"
                    value={settings.safePadding}
                    onChange={handleNumber('safePadding', 0)}
                  />
                </label>
                <label className="settings-row">
                  <span className="settings-label">
                    Scale factor
                    <HelpIcon text="Scale timecode block up/down." />
                  </span>
                  <input
                    type="number"
                    step="0.05"
                    min="0.1"
                    value={settings.scaleFactor}
                    onChange={handleNumber('scaleFactor', 1)}
                  />
                </label>
              </div>

              <div className="settings-group">
                <div className="settings-title">Diagnostics</div>
                <label className="settings-row">
                  <span className="settings-label">
                    Show diagnostics
                    <HelpIcon text="Show performance and sync stats." />
                  </span>
                  <input
                    type="checkbox"
                    checked={settings.showDiagnostics}
                    onChange={handleBool('showDiagnostics')}
                  />
                </label>
                <div className="settings-note">
                  Network: {networkStatus.status}
                  {networkStatus.driftMs != null
                    ? ` (${networkStatus.driftMs.toFixed(0)} ms drift)`
                    : ''}
                  {networkStatus.rttMs != null
                    ? ` • RTT ${networkStatus.rttMs.toFixed(0)} ms`
                    : ''}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Hidden measurement structure */}
      <div
        className="hidden-measure"
        ref={hiddenMeasureRef}
        style={{
          fontFamily: settings.fontFamily,
          fontWeight: settings.fontWeight,
          padding: `${measurePadding}px`,
        }}
      >
        {(settings.showTime || settings.showFrames) && (
          <div className="timecode-display" style={{ lineHeight: 1 }}>
            {timecodeMeasure}
            {indicatorLabel && (
              <span className="dropframe-indicator">{indicatorLabel}</span>
            )}
          </div>
        )}
        {settings.showMs && (
          <div className="millisecond-display" style={{ lineHeight: 1 }}>
            {msMeasure}
          </div>
        )}
      </div>
    </div>
  );
}

export default App;

/** Utility to Detect Screen Refresh Rate **/
function detectScreenRefreshRate() {
  return new Promise((resolve) => {
    const checks = {
      frameRateChecks: 0,
      screenFrameTime: 0,
      prevTime: 0,
    };

    const frameTimeTable = (frameTime) => {
      if (frameTime > 15.5) return 16.66666666666667; // ~60Hz
      if (frameTime > 12.5) return 13.33333333333333; // ~75Hz
      if (frameTime > 10.5) return 11.111111111111111; // ~90Hz
      if (frameTime > 9.5) return 10; // ~100Hz
      if (frameTime > 8) return 8.33333333333333; // ~120Hz
      if (frameTime > 6.5) return 6.944444444444444; // ~144Hz
      if (frameTime > 5.5) return 6.0606060606060606; // ~165Hz
      if (frameTime > 3.5) return 4.166666666666667; // ~240Hz
      if (frameTime > 2.5) return 2.777777777777778; // ~360Hz
      return 2; // ~500Hz fallback
    };

    const check = (now) => {
      const dt = now - checks.prevTime;
      checks.prevTime = now;

      if (dt > 100) {
        requestAnimationFrame(check);
        return;
      }

      if (checks.frameRateChecks < 20) {
        requestAnimationFrame(check);
        checks.frameRateChecks++;
        checks.screenFrameTime =
          checks.screenFrameTime === 0 ? dt : (checks.screenFrameTime + dt) / 2;
      } else {
        checks.screenFrameTime = frameTimeTable(checks.screenFrameTime);
        resolve(checks.screenFrameTime);
      }
    };

    requestAnimationFrame((startTime) => {
      checks.prevTime = startTime;
      requestAnimationFrame(check);
    });
  });
}